import {
  Component,
  ChangeDetectionStrategy,
  AfterViewInit,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  Graph,
  Shape,
  Node,
  Edge,
} from '@antv/x6';
import { Stencil } from '@antv/x6-plugin-stencil';
import { Transform } from '@antv/x6-plugin-transform';
import { Selection } from '@antv/x6-plugin-selection';
import { Snapline } from '@antv/x6-plugin-snapline';
import { Keyboard } from '@antv/x6-plugin-keyboard';
import { Clipboard } from '@antv/x6-plugin-clipboard';
import { History } from '@antv/x6-plugin-history';

@Component({
  selector: 'rappider-bpmn-editor',
  standalone: true,
  imports: [],
  templateUrl: './bpmn-editor.component.html',
  styleUrls: ['./bpmn-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpmnEditorComponent implements AfterViewInit {
  @ViewChild('container', { static: true }) containerRef!: ElementRef;
  @ViewChild('stencil', { static: true }) stencilRef!: ElementRef;

  private graph!: Graph;
  private stencil!: Stencil;

  ngAfterViewInit(): void {
    this.initializeGraph();
    this.loadNodes();
  }

  initializeGraph() {
    const container = this.containerRef.nativeElement;
    const stencilEl = this.stencilRef.nativeElement;

    this.graph = new Graph({
      container,
      grid: true,
      mousewheel: {
        enabled: true,
        modifiers: 'ctrl',
        minScale: 0.5,
        maxScale: 3,
      },
      connecting: {
        router: 'manhattan',
        connector: { name: 'rounded', args: { radius: 8 } },
        anchor: 'center',
        connectionPoint: 'anchor',
        allowBlank: false,
        snap: { radius: 20 },
        allowLoop: false,
        allowNode: true,
        allowEdge: false,
        highlight: true,
        allowMulti: false,
        validateConnection({ targetMagnet }) {
          return !!targetMagnet;
        },
        createEdge() {
          return new Shape.Edge({
            attrs: {
              line: {
                stroke: '#A2B1C3',
                strokeWidth: 2,
                sourceMarker: ['diamond', { size: 8 }],
                targetMarker: ['block', { size: 8 }],
              },
            },
            zIndex: 0,
            tools: [
              { name: 'button-remove', args: { distance: -40 } },
            ],
          });
        },
      },
      highlighting: {
        magnetAvailable: {
          name: 'stroke',
          args: {
            padding: 3,
            attrs: {
              strokeWidth: 3,
              stroke: '#52c41a',
            },
          },
        },
      },
    });

    this.graph
      .use(new Transform({ resizing: false, rotating: false }))
      .use(new Selection({ rubberband: true, showNodeSelectionBox: true }))
      .use(new Snapline())
      .use(new Keyboard())
      .use(new Clipboard())
      .use(new History());

    this.graph.on('node:selected', ({ node }) => {
      this.graph.getNodes().forEach(n => n.removeTools());
      node.addTools({
        name: 'boundary',
        args: {
          padding: 5,
          attrs: {
            fill: '#7c68fc',
            stroke: '#9254de',
            strokeWidth: 1,
            fillOpacity: 0.2,
          },
        },
      });
    });

    this.graph.on('node:mouseenter', ({ node }) => {
      node.getPorts().forEach(port => {
        node.portProp(port.id!, 'attrs/body/style/visibility', 'visible');
      });
    });

    this.graph.on('node:mouseleave', ({ node }) => {
      node.getPorts().forEach(port => {
        node.portProp(port.id!, 'attrs/body/style/visibility', 'hidden');
      });
    });

    this.stencil = new Stencil({
      target: this.graph,
      stencilGraphWidth: 200,
      stencilGraphHeight: 500,
      collapsable: true,
      title: 'Nodes',
      groups: [
        { title: 'BPMN Elements', name: 'group1' }
      ],
      layoutOptions: { columns: 1, rowHeight: 60 },
      getDragNode: (node) => node.clone(),
      getDropNode: (node) => {
        const type = node.data?.type;
        return this.graph.createNode(this.getGraphNodeConfig(type));
      },
    });

    stencilEl.appendChild(this.stencil.container);
  }

  loadNodes() {
    this.registerCustomNodes();

    const event = this.graph.createNode({
      shape: 'rect',
      width: 180,
      height: 50,
      label: 'Event',
      data: { type: 'event' },
      attrs: {
        body: { fill: '#fff', stroke: '#aaa', rx: 8, ry: 8 },
        label: { fontSize: 13, fill: '#000', refX: 10, textAnchor: 'start' },
      },
    });

    const aiStep = this.graph.createNode({
      shape: 'rect',
      width: 180,
      height: 50,
      label: 'AI Step',
      data: { type: 'ai' },
      attrs: {
        body: { fill: '#fff', stroke: '#aaa', rx: 8, ry: 8 },
        label: { fontSize: 13, fill: '#000', refX: 10, textAnchor: 'start' },
      },
    });

    const customStep = this.graph.createNode({
      shape: 'rect',
      width: 180,
      height: 50,
      label: 'Custom Code',
      data: { type: 'code' },
      attrs: {
        body: { fill: '#fff', stroke: '#aaa', rx: 8, ry: 8 },
        label: { fontSize: 13, fill: '#000', refX: 10, textAnchor: 'start' },
      },
    });

    const endpointStep = this.graph.createNode({
      shape: 'rect',
      width: 180,
      height: 50,
      label: 'Endpoint',
      data: { type: 'endpoint' },
      attrs: {
        body: { fill: '#fff', stroke: '#aaa', rx: 8, ry: 8 },
        label: { fontSize: 13, fill: '#000', refX: 10, textAnchor: 'start' },
      },
    });

    this.stencil.load([event, aiStep, customStep, endpointStep], 'group1');
  }

  registerCustomNodes() {
    const register = (name: string, stroke: string, fill: string) => {
      Graph.registerNode(
        name,
        {
          inherit: 'rect',
          width: 180,
          height: 50,
          markup: [
            { tagName: 'rect', selector: 'body' },
            { tagName: 'text', selector: 'label' }
          ],
          attrs: {
            body: {
              stroke,
              fill,
              rx: 8,
              ry: 8,
            },
            label: {
              refX: 10,
              refY: 25,
              textAnchor: 'start',
              textVerticalAnchor: 'middle',
              fontSize: 14,
              fill: '#000',
            },
          },
          ports: this.getPortsConfig(),
        },
        true
      );
    };

    Graph.registerNode(
      'event-start',
      {
        inherit: 'circle',
        width: 45,
        height: 45,
        attrs: {
          body: {
            stroke: '#1890ff',
            strokeWidth: 2,
            fill: '#fff',
          },
          label: {
            fontSize: 14,
            fill: '#000',
          },
        },
        tools: [
          {
            name: 'node-editor',
            args: { attrs: { backgroundColor: '#EFF4FF' } },
          },
        ],
        ports: this.getPortsConfig(),
      },
      true
    );

    register('ai-step-function', '#52c41a', '#f6ffed');
    register('custom-code-step-function', '#faad14', '#fffbe6');
    register('endpoint-step-function', '#13c2c2', '#e6fffb');
  }

  getPortsConfig() {
    return {
      groups: {
        top: this.portGroup('top'),
        right: this.portGroup('right', true),
        bottom: this.portGroup('bottom'),
        left: this.portGroup('left'),
      },
      items: [
        { group: 'top' },
        { group: 'right' },
        { group: 'bottom' },
        { group: 'left' },
      ],
    };
  }

  portGroup(position: string, isOutput = false) {
    return {
      position,
      attrs: {
        body: {
          width: 10,
          height: 10,
          magnet: true,
          fill: '#fff',
          stroke: '#5F95FF',
          strokeWidth: 1,
          style: {
            visibility: 'hidden',
          },
        },
      },
      markup: [
        {
          tagName: 'rect',
          selector: 'body',
        },
      ],
      label: {
        position: {
          name: 'center',
        },
      },
    };
  }

  getGraphNodeConfig(type: string): any {
    switch (type) {
      case 'event':
        return { shape: 'event-start', label: 'Event' };
      case 'ai':
        return { shape: 'ai-step-function', label: 'AI Step' };
      case 'code':
        return { shape: 'custom-code-step-function', label: 'Custom Code' };
      case 'endpoint':
        return { shape: 'endpoint-step-function', label: 'Endpoint' };
      default:
        return { shape: 'rect', label: 'Unknown' };
    }
  }
}
